import React, { useState, useEffect, FormEvent } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { StopSummary } from 'components/marketplace/StopSummary'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, createStyles, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { IFullStop, IShipmentsState } from 'types'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { UploadDocument } from 'apps/detention-review/components/UploadDocument'
import useDetention from 'apps/detention-review/utils/hooks/useDetention'
import { useUserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useCreateShipmentDetention } from 'apps/fleet-planner/types/apiTypes'
import { TextInput } from 'components/atoms'
import { LaneArrowIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { stopsSummary } from 'utils/shipmentHelpers'
import { updateShipmentAccessorials } from '../utils/updateShipmentAccessorials'
import { DateTimeInputs } from './DateTimeInputs'
import { FacilitySelect } from './FacilitySelect'
import { RequestSummary } from './RequestSummary'

export interface ISelector {
  shipmentUpdate: IShipmentsState
}

const useStyles = makeStyles(theme =>
  createStyles({
    mb1: {
      marginBottom: theme.spacing(1),
    },
    row: {
      marginLeft: theme.spacing(-1),
    },
  })
)

export const DetentionRequestForm = React.memo(() => {
  const { trackEvent } = useFMSAnalytics()
  const classes = useStyles()
  const history = useHistory()
  const { errors: error, getMarketplaceLoad, shipmentDetails: shipment } = useMarketplaceContext()
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const { timeFormats } = useUserContext()

  const { loading: isLoading, mutate: createDetention } = useCreateShipmentDetention({
    id: parseInt(shipmentId, 10),
  })

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selectedStop, setSelectedStop] = useState<IFullStop>()

  const { stops = [], equipment } = shipment || {}

  const {
    calculatedAmount,
    detention,
    isFormValid,
    onChange,
    onChangeDateTime,
    onChangeText,
    setDetention,
  } = useDetention(selectedStop, equipment)
  useEffect(() => {
    const selectedFacilityStop = stops.find(
      stop => stop.facility.shipperFacilityUuid === detention.shipperFacilityUuid
    )
    if (selectedFacilityStop) setSelectedStop(selectedFacilityStop)
  }, [detention.shipperFacilityUuid, stops])

  const { additionalInfo: { externalNote = '', submittedBy = '' } = {} } = detention || {}
  const { appointment, status: { arrivedAt = '', leftAt = '', timezone = '' } = {} } =
    selectedStop || {}
  const hasFile = !!detention.fileName || !!detention.fileFileName
  const disableSubmit = isFormValid && submittedBy
  const isSubmitDisbled = !disableSubmit || isLoading
  const stateHeading = detention.state ? `(${capitalize(detention.state)})` : ''
  const heading = `Detention Request ${stateHeading}`
  const { delivery, pickup } = stopsSummary(shipment)

  useEffect(() => {
    if (shipmentId && !shipment?.id) {
      getMarketplaceLoad(shipmentId)
    }
  }, [getMarketplaceLoad, shipment?.id, shipmentId])

  useEffect(() => {
    if (error) {
      setErrorMessage(error)
    }
  }, [error])

  useEffect(() => {
    // if upload POD remove inline Error
    if (hasFile) {
      setErrorMessage('')
    }
  }, [detention.fileName, hasFile])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSubmitDisbled) {
      setErrorMessage('Please fill out all required fields and ensure that they are valid')
    } else if (!hasFile) {
      setErrorMessage(`Please upload a POD ${error || ''}`)
    } else {
      setErrorMessage('')
      const updateAccessorials = updateShipmentAccessorials({
        detention,
        selectedStop,
        calculatedAmount,
        timeFormats,
      })
      trackEvent('Detention Review', 'SUBMITTED_DETENTION_FORM', { Shipment: shipmentId })
      try {
        await createDetention(updateAccessorials)
        history.push(`/detention-request/${shipmentId}/success`)
      } catch (e) {
        setErrorMessage('Failed to submit detention request')
      }
    }
  }

  if (!shipment) return <></>
  return (
    <Box pl={2} pr={1} pt={2}>
      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} xl={4}>
            <Card>
              <Grid container>
                <Grid item xs={12} md={12}>
                  <Typography variant='h2' className='fl'>
                    {heading}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                  <span>Load {shipment?.id} - </span>
                  {pickup && delivery && (
                    <span>
                      <StopSummary stop={pickup} variation='inline-primary' />
                      <LaneArrowIcon className='inline mh1' />
                      <StopSummary stop={delivery} variation='inline-primary' />
                    </span>
                  )}
                </Grid>
                <Grid item xs={12} md={12}>
                  <FacilitySelect
                    stops={stops}
                    detention={detention}
                    onSelect={setDetention}
                    setSelectedStop={setSelectedStop}
                    timeFormats={timeFormats}
                  />
                  {detention.shipperFacilityUuid && selectedStop?.status && (
                    <DateTimeInputs
                      appointment={appointment}
                      label='Check In'
                      timeStamp={detention.startAt || arrivedAt}
                      timezone={timezone}
                      id='startDateTime'
                      onChange={onChangeDateTime}
                    />
                  )}
                  {detention.shipperFacilityUuid && selectedStop?.status && (
                    <DateTimeInputs
                      appointment={appointment}
                      label='Check Out'
                      timeStamp={detention.endAt || leftAt}
                      timezone={timezone}
                      id='endDateTime'
                      onChange={onChangeDateTime}
                    />
                  )}
                  {detention.shipperFacilityUuid && (
                    <RequestSummary
                      startDateTime={
                        detention.startDateTime || detention.additionalInfo.startDateTime
                      }
                      endDateTime={detention.endDateTime || detention.additionalInfo.endDateTime}
                      appointment={appointment}
                      equipment={shipment.equipment}
                      className={classes.row}
                    />
                  )}
                </Grid>
                {detention.shipperFacilityUuid && (
                  <Grid item xs={12} md={12}>
                    {detention.shipperFacilityUuid && (
                      <div className={classes.row}>
                        <strong>POD</strong>
                      </div>
                    )}
                    {detention.shipperFacilityUuid && (
                      <UploadDocument
                        editMode={false}
                        fileUrl={detention.fileUrl}
                        fileName={detention.fileName}
                        onChange={setDetention}
                        isAdmin={false}
                      />
                    )}
                  </Grid>
                )}
                {detention.shipperFacilityUuid && (
                  <Grid item xs={12} md={12} className={classes.mb1}>
                    <label htmlFor='externalNote'>Notes (Optional)</label>
                    <textarea id='externalNote' value={externalNote} onChange={onChangeText} />
                  </Grid>
                )}
                {detention.shipperFacilityUuid && (
                  <Grid item xs={12} md={12} className={classes.mb1}>
                    <TextInput
                      className='fs-mask'
                      id='submittedBy'
                      label='Email'
                      value={submittedBy}
                      placeholder='Enter email'
                      onChange={onChange}
                    />
                  </Grid>
                )}
                {detention.shipperFacilityUuid && (
                  <Grid item xs={12} md={12}>
                    {errorMessage && (
                      <Grid item md={12} className={classes.mb1}>
                        <InlineMessage
                          message={errorMessage}
                          type={INLINE_MESSAGE_VARIANTS.ALERT}
                        />
                      </Grid>
                    )}
                    <button
                      className='btn-primary btn-full-width'
                      type='submit'
                      data-test='submit-detention-request'
                      disabled={isSubmitDisbled}>
                      Submit Detention Request
                    </button>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
})
